import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Building2, UserRound } from "lucide-react";
import { SEO } from "../components/SEO";
import { GoogleSignInButton, AuthDivider } from "../components/GoogleSignInButton";
import { API, LOGO_MARK } from "../lib/site";
import { HOTEL_TOKEN_KEY } from "./Join";
import { MEMBER_TOKEN_KEY } from "./MemberRegister";

const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

const LoginForm = ({ testid, endpoint, tokenKey, onSuccess, fieldsTestid }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/${endpoint}`, { email, password });
            localStorage.setItem(tokenKey, data.token);
            onSuccess();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid={testid}>
            <label htmlFor={`${fieldsTestid}-email`} className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                Email
            </label>
            <input
                id={`${fieldsTestid}-email`}
                data-testid={`${fieldsTestid}-email`}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="you@example.com"
                autoComplete="username"
            />
            <label htmlFor={`${fieldsTestid}-password`} className="mt-5 mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                Password
            </label>
            <input
                id={`${fieldsTestid}-password`}
                data-testid={`${fieldsTestid}-password`}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                autoComplete="current-password"
            />
            {error && (
                <p data-testid={`${fieldsTestid}-error`} role="alert" className="mt-4 text-sm font-semibold text-[#E8A0A0]">
                    {error}
                </p>
            )}
            <button
                type="submit"
                data-testid={`${fieldsTestid}-submit`}
                disabled={busy}
                className="btn-gold mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60"
            >
                {busy ? "Signing in…" : "Sign In"}
            </button>
            <p className="mt-4 text-center text-xs text-[#FDFBF7]/50">
                <Link to="/forgot-password" data-testid={`${fieldsTestid}-forgot`} className="font-bold text-[#D9B96A] hover:underline">
                    Forgot password?
                </Link>
            </p>
        </form>
    );
};

const Login = () => {
    const [params] = useSearchParams();
    const [tab, setTab] = useState(params.get("tab") === "member" ? "member" : "hotel");
    const navigate = useNavigate();

    return (
        <main>
            <SEO title="Login | Global Green Hotel" description="Sign in to your GGH hotel or member account." />
            <div className="grain flex min-h-screen items-center justify-center bg-[#04150D] px-5 py-32">
                <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#07211A] p-9" data-testid="login-page">
                    <img src={LOGO_MARK} alt="Global Green Hotel logo" className="w-36" />
                    <h1 className="font-display mt-7 text-3xl font-medium text-[#FDFBF7]">Welcome back</h1>

                    <div className="mt-7 grid grid-cols-2 gap-2 rounded-full border border-white/10 bg-[#04150D] p-1.5" role="tablist" aria-label="Account type">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === "hotel"}
                            data-testid="login-tab-hotel"
                            onClick={() => setTab("hotel")}
                            className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors duration-200 ${
                                tab === "hotel" ? "bg-[#C2A160] text-[#0A1711]" : "text-[#FDFBF7]/60 hover:text-[#D9B96A]"
                            }`}
                        >
                            <Building2 size={15} aria-hidden="true" /> Hotel
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === "member"}
                            data-testid="login-tab-member"
                            onClick={() => setTab("member")}
                            className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors duration-200 ${
                                tab === "member" ? "bg-[#C2A160] text-[#0A1711]" : "text-[#FDFBF7]/60 hover:text-[#D9B96A]"
                            }`}
                        >
                            <UserRound size={15} aria-hidden="true" /> Member
                        </button>
                    </div>

                    <div className="mt-8">
                        {tab === "hotel" ? (
                            <>
                                <LoginForm
                                    testid="hotel-login-form-tab"
                                    fieldsTestid="tab-hotel-login"
                                    endpoint="hotels/login"
                                    tokenKey={HOTEL_TOKEN_KEY}
                                    onSuccess={() => navigate("/hotel/dashboard")}
                                />
                                <p className="mt-6 text-center text-xs text-[#FDFBF7]/50">
                                    No hotel account yet?{" "}
                                    <Link to="/join" data-testid="login-join-hotel" className="font-bold text-[#D9B96A] hover:underline">
                                        Join GGH
                                    </Link>
                                </p>
                            </>
                        ) : (
                            <>
                                <GoogleSignInButton testid="member-google-signin" />
                                <AuthDivider />
                                <LoginForm
                                    testid="member-login-form-tab"
                                    fieldsTestid="tab-member-login"
                                    endpoint="members/login"
                                    tokenKey={MEMBER_TOKEN_KEY}
                                    onSuccess={() => navigate("/member/dashboard")}
                                />
                                <p className="mt-6 text-center text-xs text-[#FDFBF7]/50">
                                    Not a member yet?{" "}
                                    <Link to="/register" data-testid="login-join-member" className="font-bold text-[#D9B96A] hover:underline">
                                        Create a member account
                                    </Link>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Login;
